// 客服实时通道（WebSocket）：协议与 app 一致
// - 收到 welcome 后订阅；服务端约 3 秒一个 ping，10 秒没有任何帧就判定断线重连
// - 重连指数退避 2s → 30s 封顶；页面回到前台 / 网络恢复时立刻重连
// - 页面可见时每 60 秒报一次在线，坐席端据此显示访客在线
export default class CsCable {
  constructor({ url, pubsubToken, onEvent, onState, onReconnected, onRejected }) {
    this.url = url
    this.identifier = JSON.stringify({ channel: 'RoomChannel', pubsub_token: pubsubToken })
    this.onEvent = onEvent
    this.onState = onState || (() => {})
    this.onReconnected = onReconnected || (() => {})
    this.onRejected = onRejected || (() => {})
    this.ws = null
    this.attempts = 0
    this.everConnected = false
    this.disposed = false
    this.state = 'disconnected'
    this.watchdog = null
    this.presenceTimer = null
    this.reconnectTimer = null
    this.onVisible = () => {
      if (document.visibilityState !== 'visible') return
      if (this.state === 'disconnected') this.reconnectNow()
      else if (this.state === 'connected') this.perform('update_presence')
    }
    this.onOnline = () => this.state === 'disconnected' && this.reconnectNow()
  }

  connect() {
    document.addEventListener('visibilitychange', this.onVisible)
    window.addEventListener('online', this.onOnline)
    this.open()
  }

  dispose() {
    this.disposed = true
    document.removeEventListener('visibilitychange', this.onVisible)
    window.removeEventListener('online', this.onOnline)
    clearTimeout(this.reconnectTimer)
    this.closeSocket()
  }

  setState(state) {
    if (this.state === state) return
    this.state = state
    this.onState(state)
  }

  open() {
    if (this.disposed) return
    this.setState('connecting')
    this.closeSocket()
    let ws
    try {
      ws = new WebSocket(this.url)
    } catch (e) {
      this.scheduleReconnect()
      return
    }
    this.ws = ws
    ws.onmessage = e => this.onFrame(e.data)
    ws.onclose = () => this.ws === ws && this.scheduleReconnect()
    ws.onerror = () => this.ws === ws && this.scheduleReconnect()
    this.kickWatchdog()
  }

  onFrame(raw) {
    this.kickWatchdog()
    let frame
    try {
      frame = JSON.parse(raw)
    } catch (e) {
      return
    }
    switch (frame.type) {
      case 'welcome':
        this.send({ command: 'subscribe', identifier: this.identifier })
        return
      case 'confirm_subscription':
        this.attempts = 0
        this.setState('connected')
        this.startPresence()
        if (this.everConnected) this.onReconnected()
        this.everConnected = true
        return
      case 'ping':
        return
      case 'disconnect':
        this.scheduleReconnect()
        return
      case 'reject_subscription':
        // 访客身份在客服系统里已被删除：重连没用，交给上层重新建访客
        this.closeSocket()
        this.setState('disconnected')
        this.onRejected()
        return
    }
    const message = frame.message
    if (message && typeof message.event === 'string') {
      this.onEvent(message.event, message.data || {})
    }
  }

  startPresence() {
    clearInterval(this.presenceTimer)
    const report = () => document.visibilityState === 'visible' && this.perform('update_presence')
    report()
    this.presenceTimer = setInterval(report, 60000)
  }

  perform(action) {
    this.send({ command: 'message', identifier: this.identifier, data: JSON.stringify({ action }) })
  }

  send(payload) {
    try {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(payload))
    } catch (e) {
      // 发送失败等看门狗判定断线后重连
    }
  }

  kickWatchdog() {
    clearTimeout(this.watchdog)
    this.watchdog = setTimeout(() => this.scheduleReconnect(), 10000)
  }

  scheduleReconnect() {
    if (this.disposed || this.reconnectTimer) return
    this.closeSocket()
    this.setState('disconnected')
    const delay = Math.min(30, 2 << Math.min(this.attempts, 4))
    this.attempts += 1
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.open()
    }, delay * 1000)
  }

  reconnectNow() {
    clearTimeout(this.reconnectTimer)
    this.reconnectTimer = null
    this.attempts = 0
    this.open()
  }

  closeSocket() {
    clearTimeout(this.watchdog)
    clearInterval(this.presenceTimer)
    const ws = this.ws
    this.ws = null
    if (ws) {
      ws.onmessage = ws.onclose = ws.onerror = null
      try {
        ws.close()
      } catch (e) {
        // 忽略
      }
    }
  }
}
